import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { EditorTool, EditorMode, ClipboardItem, BorderStyle } from '../types';
import type { SceneEffectInstance } from '../types/sceneEffects';
import { randomCharacterSeed, type CharacterPresetId } from '../util/characterPresets';
import { useSelectionStore } from './selectionStore';

/** Toolbar'dan istenen, Stage'in sahneye yazacağı preset karakter. */
export interface CharacterInsertRequest {
  preset: CharacterPresetId;
  seed: number;
  /** Her istekte artan kimlik; istek temizlense de tekrar etmez. */
  nonce: number;
}

let characterInsertCounter = 0;

/** Bu araçlara geçildiğinde sahne nesnesi seçimi kalkar; çizim hedefi `currentLayer` olur. */
const TOOLS_THAT_CLEAR_SCENE_SELECTION: readonly EditorTool[] = [
  'line',
  'rectangle',
  'circle',
  'triangle',
  'curve',
  'polygon',
  'text',
];

export interface ChatMessageDebug {
  jobs: unknown[];
  actions: unknown[];
  payloads: unknown[];
  executionResults: Array<{
    kind: 'payload' | 'action' | 'job';
    index: number;
    status: 'applied' | 'queued' | 'skipped' | 'failed';
    detail: string;
  }>;
  receivedAt: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  debug?: ChatMessageDebug;
}

export interface ChatConversation {
  id: number;
  title: string | null;
  createdAt: string;
  lastMessageAt: string | null;
}

interface EditorState {
  // State
  mode: EditorMode;
  aiChatVisible: boolean;
  aiChatText: string;
  aiChatWidth: number;
  aiChatHistory: ChatMessage[];
  aiChatMovieId: number | null;
  aiChatContext: string | null;
  aiChatConversations: ChatConversation[];
  aiChatActiveConversationId: number | null;
  aiChatDebugMode: boolean;
  aiSamplePrompts: string[];
  currentTool: EditorTool;
  currentFrame: number;
  fps: number;
  isPlaying: boolean;
  clipboard: ClipboardItem | null;
  showGrid: boolean;
  snapToGrid: boolean;
  gridSize: number;
  zoom: number;
  fillColor: string;
  strokeColor: string;
  strokeWidth: number;
  strokeStyle: BorderStyle;
  sceneEffects: SceneEffectInstance[];
  pendingCharacterInsert: CharacterInsertRequest | null;

  // Actions
  setMode: (mode: EditorMode) => void;
  setAiChatVisible: (visible: boolean) => void;
  setAiChatText: (text: string) => void;
  setAiChatWidth: (width: number) => void;
  setAiChatHistory: (history: ChatMessage[], movieId?: number | null) => void;
  addAiChatMessage: (message: ChatMessage) => void;
  removeAiChatMessage: (id: string) => void;
  setAiChatContext: (context: string | null) => void;
  resetAiChat: () => void;
  setAiChatMovieId: (movieId: number | null) => void;
  setAiChatConversations: (conversations: ChatConversation[]) => void;
  setAiChatActiveConversationId: (id: number | null) => void;
  addAiChatConversation: (conversation: ChatConversation) => void;
  removeAiChatConversation: (id: number) => void;
  setAiChatDebugMode: (enabled: boolean) => void;
  toggleAiChatDebugMode: () => void;
  setAiSamplePrompts: (prompts: string[]) => void;
  setTool: (tool: EditorTool) => void;
  setFrame: (frame: number) => void;
  setFps: (fps: number) => void;
  play: () => void;
  pause: () => void;
  stop: () => void;
  togglePlay: () => void;
  nextFrame: () => void;
  prevFrame: () => void;
  setClipboard: (item: ClipboardItem | null) => void;
  setShowGrid: (show: boolean) => void;
  setSnapToGrid: (snap: boolean) => void;
  setGridSize: (size: number) => void;
  setZoom: (zoom: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  setFillColor: (color: string) => void;
  setStrokeColor: (color: string) => void;
  setStrokeWidth: (width: number) => void;
  setStrokeStyle: (style: BorderStyle) => void;
  setSceneEffects: (effects: SceneEffectInstance[]) => void;
  upsertSceneEffect: (effect: SceneEffectInstance) => void;
  removeSceneEffect: (effectId: number) => void;
  requestCharacterInsert: (preset: CharacterPresetId) => void;
  clearCharacterInsert: () => void;
}

export const useEditorStore = create<EditorState>()(
  persist(
  (set) => ({
  // Initial state
  mode: 'edit',
  aiChatVisible: true,
  aiChatText: '',
  aiChatWidth: 500,
  aiChatHistory: [],
  aiChatMovieId: null,
  aiChatContext: null,
  aiChatConversations: [],
  aiChatActiveConversationId: null,
  aiChatDebugMode: false,
  aiSamplePrompts: [
    "Create a scene with a man and a woman walking in a park.",
    "A rainy street, with cars passing slowly. Storefronts are open. A man is walking alone, looking for a place to eat.",
    "A sunny beach, with a few people swimming and sunbathing. A few boats are anchored in the water. A man is walking on the beach, looking for a place to eat.",
  ],
  currentTool: 'select',
  currentFrame: 0,
  fps: 24,
  isPlaying: false,
  clipboard: null,
  showGrid: false,
  snapToGrid: false,
  gridSize: 10,
  zoom: 1,
  fillColor: '#3b82f6',
  strokeColor: '#000000',
  strokeWidth: 2,
  strokeStyle: 'solid',
  sceneEffects: [],
  pendingCharacterInsert: null,

  // Actions
  setMode: (mode) => set({ mode }),

  setAiChatVisible: (visible) => set({ aiChatVisible: visible }),

  setAiChatText: (text) => set({ aiChatText: text }),

  setAiChatWidth: (width) => set({ aiChatWidth: Math.max(200, Math.min(800, width)) }),

  setAiChatHistory: (history, movieId) => set((state) => ({ 
    aiChatHistory: history,
    aiChatMovieId: movieId !== undefined ? movieId : state.aiChatMovieId,
  })),

  addAiChatMessage: (message) => set((state) => ({ 
    aiChatHistory: [...state.aiChatHistory, message] 
  })),

  removeAiChatMessage: (id) => set((state) => ({
    aiChatHistory: state.aiChatHistory.filter((m) => m.id !== id),
  })),

  setAiChatContext: (context) => set({ aiChatContext: context }),

  setAiChatMovieId: (movieId) => set({ aiChatMovieId: movieId }),

  setAiChatConversations: (conversations) => set({ aiChatConversations: conversations }),

  setAiChatActiveConversationId: (id) => set({ aiChatActiveConversationId: id }),

  addAiChatConversation: (conversation) => set((state) => ({
    aiChatConversations: [conversation, ...state.aiChatConversations],
  })),

  setAiChatDebugMode: (enabled) => set({ aiChatDebugMode: enabled }),

  toggleAiChatDebugMode: () => set((state) => ({ aiChatDebugMode: !state.aiChatDebugMode })),

  removeAiChatConversation: (id) => set((state) => {
    const filtered = state.aiChatConversations.filter(c => c.id !== id);
    const isActive = state.aiChatActiveConversationId === id;
    return {
      aiChatConversations: filtered,
      // If we removed the active tab, select the first remaining or null
      aiChatActiveConversationId: isActive 
        ? (filtered.length > 0 ? filtered[0].id : null)
        : state.aiChatActiveConversationId,
      // Clear history if active tab was removed
      ...(isActive ? { aiChatHistory: [] } : {}),
    };
  }),

  resetAiChat: () => set({ 
    aiChatText: '', 
    aiChatHistory: [], 
    aiChatMovieId: null,
    aiChatContext: null,
    aiChatConversations: [],
    aiChatActiveConversationId: null,
  }),

  setAiSamplePrompts: (prompts) => set({ aiSamplePrompts: prompts }),

  setTool: (tool) => {
    if (TOOLS_THAT_CLEAR_SCENE_SELECTION.includes(tool)) {
      useSelectionStore.getState().clearSelection();
    }
    set({ currentTool: tool });
  },

  setFrame: (frame) => set({ currentFrame: Math.max(0, frame) }),

  setFps: (fps) => set({ fps: Math.max(1, Math.min(60, fps)) }),

  play: () => set({ isPlaying: true }),

  pause: () => set({ isPlaying: false }),

  stop: () => set({ isPlaying: false, currentFrame: 0 }),

  togglePlay: () => set((state) => ({ isPlaying: !state.isPlaying })),

  nextFrame: () => set((state) => ({ currentFrame: state.currentFrame + 1 })),

  prevFrame: () =>
    set((state) => ({ currentFrame: Math.max(0, state.currentFrame - 1) })),

  setClipboard: (item) => set({ clipboard: item }),

  setShowGrid: (show) => set({ showGrid: show }),

  setSnapToGrid: (snap) => set({ snapToGrid: snap }),

  setGridSize: (size) => set({ gridSize: Math.max(1, size) }),

  setZoom: (zoom) => set({ zoom: Math.max(0.1, Math.min(4, zoom)) }),

  zoomIn: () =>
    set((state) => ({ zoom: Math.min(4, state.zoom * 1.25) })),

  zoomOut: () =>
    set((state) => ({ zoom: Math.max(0.1, state.zoom / 1.25) })),

  resetZoom: () => set({ zoom: 1 }),

  setFillColor: (color) => set({ fillColor: color }),

  setStrokeColor: (color) => set({ strokeColor: color }),

  setStrokeWidth: (width) => set({ strokeWidth: Math.max(0, Math.min(40, width)) }),

  setStrokeStyle: (style) => set({ strokeStyle: style }),

  setSceneEffects: (effects) => set({ sceneEffects: effects }),

  upsertSceneEffect: (effect) =>
    set((state) => {
      const existingIndex = state.sceneEffects.findIndex(
        (item) => item.id === effect.id || item.effectType === effect.effectType
      );
      if (existingIndex === -1) {
        return { sceneEffects: [...state.sceneEffects, effect] };
      }
      const next = [...state.sceneEffects];
      next[existingIndex] = effect;
      return { sceneEffects: next };
    }),

  removeSceneEffect: (effectId) =>
    set((state) => ({
      sceneEffects: state.sceneEffects.filter((effect) => effect.id !== effectId),
    })),

  requestCharacterInsert: (preset) =>
    set({
      currentTool: 'select',
      pendingCharacterInsert: {
        preset,
        seed: randomCharacterSeed(),
        nonce: ++characterInsertCounter,
      },
    }),

  clearCharacterInsert: () => set({ pendingCharacterInsert: null }),
}),
  {
    name: 'prototype-editor',
    partialize: (state) => ({
      aiChatHistory: state.aiChatHistory,
      aiChatMovieId: state.aiChatMovieId,
      aiChatContext: state.aiChatContext,
      aiChatWidth: state.aiChatWidth,
      aiChatConversations: state.aiChatConversations,
      aiChatActiveConversationId: state.aiChatActiveConversationId,
      aiChatDebugMode: state.aiChatDebugMode,
      fillColor: state.fillColor,
      strokeColor: state.strokeColor,
      strokeWidth: state.strokeWidth,
      strokeStyle: state.strokeStyle,
    }),
  }
));
